import React, { useState, useRef, useCallback, useEffect } from "react";
import { Card, CardContent, IconButton, Typography } from "@material-ui/core";
import { ChevronLeft, ChevronRight } from "@material-ui/icons";
import Chart from "../Charts/Chart/Chart";
import CategoryGridExpense from "./CategoryGridExpense/CategoryGridExpense";

const Slider = (props) => {
  const [screen, setScreen] = useState(1);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const minSwipeDistance = 50;
  const panel1Ref = useRef(null);
  const panel2Ref = useRef(null);

  const handleTouchStart = useCallback((e) => {
    if (window.innerWidth >= 768) {
      return;
    }
    touchStartX.current = e.targetTouches[0].clientX;
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (window.innerWidth >= 768) {
      return;
    }
    touchEndX.current = e.targetTouches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (window.innerWidth >= 768) {
      return;
    }

    if (!touchStartX.current || !touchEndX.current) {
      return;
    }

    const distance = touchStartX.current - touchEndX.current;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && screen < 2) {
      setScreen(screen + 1);
    }
    if (isRightSwipe && screen > 1) {
      setScreen(screen - 1);
    }

    touchStartX.current = 0;
    touchEndX.current = 0;
  }, [screen]);

  const goToPrevious = () => {
    if (screen > 1) {
      setScreen(screen - 1);
    }
  };

  const goToNext = () => {
    if (screen < 2) {
      setScreen(screen + 1);
    }
  };

  // Reset scroll to top when switching screens so each page height is independent (mobile: always see totals graph when opening page 2)
  useEffect(() => {
    // Reset ONLY the target panel's scroll (the one we're switching TO)
    const scrollToTop = () => {
      const targetPanel = screen === 1 ? panel1Ref.current : panel2Ref.current;
      if (targetPanel) {
        // Force scroll to absolute top
        targetPanel.scrollTop = 0;
        // Also try scrollTo for compatibility
        if (targetPanel.scrollTo) {
          targetPanel.scrollTo(0, 0);
        }
      }
      
      // CRITICAL: Also reset body/window scroll on mobile (this is what was causing the shared scroll issue)
      if (window.innerWidth < 768) {
        window.scrollTo(0, 0);
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
      }
    };

    // Scroll immediately
    scrollToTop();
    
    // Scroll again after a tick and after transition
    requestAnimationFrame(scrollToTop);
    const timeoutId = setTimeout(scrollToTop, 350);
    
    return () => clearTimeout(timeoutId);
  }, [screen]);

  // Footer height is approximately 64px (py-4 = 16px top + 16px bottom + content ~32px)
  const footerHeight = 64;
  
  return (
    <div className="w-full flex flex-col overflow-hidden relative min-h-0 flex-1" style={{ minHeight: "400px", maxWidth: "100%" }}>
      {/* Main slider container */}
      <div
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        className="w-full flex-1 min-h-0 relative bg-slate-50 rounded-lg overflow-hidden pb-20 md:pb-0"
      >
        {/* Sliding wrapper */}
        <div 
          className="flex transition-transform duration-300 ease-in-out h-full"
          style={{ 
            width: "200%",
            transform: `translateX(-${(screen - 1) * 50}%)`
          }}
        >
          {/* Screen 1: Category Breakdown (Category Grid Expense) */}
          <div
            ref={panel1Ref}
            className="w-1/2 h-full overflow-y-auto overflow-x-hidden flex-shrink-0 overscroll-contain"
            style={{ WebkitOverflowScrolling: "touch", isolation: "isolate" }}
          >
            <div className="w-full p-4 md:p-8">
              <div className="w-full max-w-4xl mx-auto">
                <CategoryGridExpense
                  load={true}
                  category={props.category}
                  symbol={props.symbol}
                  categoriesWithBudget={props.categoriesWithBudget}
                />
              </div>
            </div>
          </div>

          {/* Screen 2: Revenue Vs Expense Bar Chart */}
          <div
            ref={panel2Ref}
            className="w-1/2 h-full overflow-y-auto overflow-x-hidden flex-shrink-0 overscroll-contain"
            style={{ WebkitOverflowScrolling: "touch", isolation: "isolate" }}
          >
            <div className="w-full px-2 pt-2 md:p-8">
              <Card className="shadow-lg w-full" style={{ maxWidth: "100%", overflow: "hidden" }}>
                <CardContent className="p-1 md:p-6 lg:p-10" style={{ overflow: "hidden" }}>
                  {props.screen_2_bar && props.screen_2_bar.series && props.screen_2_bar.series[0] && props.screen_2_bar.series[0].data ? (
                    <Chart data={props.screen_2_bar} />
                  ) : (
                    <div className="flex items-center justify-center h-64">
                      <Typography variant="body1" className="text-slate-500">
                        Loading chart data...
                      </Typography>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Footer with navigation - fixed at bottom on mobile */}
      <div className="fixed bottom-0 left-0 right-0 md:relative md:bottom-auto md:left-auto md:right-auto flex justify-center items-center bg-white border-t border-slate-200 py-4 md:mt-4 rounded-t-lg md:rounded-lg shadow-sm flex-shrink-0 z-20" style={{ height: "64px" }}>
        {/* Previous button - hidden on mobile, visible on desktop */}
        <IconButton
          onClick={goToPrevious}
          disabled={screen === 1}
          className="hidden md:flex"
          aria-label="Previous screen"
          style={{
            color: screen === 1 ? "#cbd5e1" : "#00779b",
          }}
        >
          <ChevronLeft />
        </IconButton>

        {/* Dot indicators */}
        <div className="flex items-center justify-center gap-3 px-6">
          <button
            onClick={() => setScreen(1)}
            className={`w-3 h-3 rounded-full transition-all duration-200 ${
              screen === 1
                ? "bg-[#00779b] w-8"
                : "bg-slate-300 hover:bg-slate-400"
            }`}
            aria-label="Screen 1"
          />
          <button
            onClick={() => setScreen(2)}
            className={`w-3 h-3 rounded-full transition-all duration-200 ${
              screen === 2
                ? "bg-[#00779b] w-8"
                : "bg-slate-300 hover:bg-slate-400"
            }`}
            aria-label="Screen 2"
          />
        </div>

        {/* Next button - hidden on mobile, visible on desktop */}
        <IconButton
          onClick={goToNext}
          disabled={screen === 2}
          className="hidden md:flex"
          aria-label="Next screen"
          style={{
            color: screen === 2 ? "#cbd5e1" : "#00779b",
          }}
        >
          <ChevronRight />
        </IconButton>
      </div>
    </div>
  );
};

export default Slider;
